import React, { createContext, useContext, useEffect, useState } from 'react';

export type ColorScheme = 'light' | 'dark';

interface ThemeContextType {
  scheme: ColorScheme;
  setScheme: (scheme: ColorScheme) => void;
  toggleScheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getInitialScheme = (): ColorScheme => {
  const saved = localStorage.getItem('color-scheme');
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scheme, setScheme] = useState<ColorScheme>(getInitialScheme);

  useEffect(() => {
    localStorage.setItem('color-scheme', scheme);
    document.documentElement.classList.toggle('dark', scheme === 'dark');
  }, [scheme]);

  const toggleScheme = () => setScheme(prev => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{ scheme, setScheme, toggleScheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
